import {
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterField,
  type PayrollRosterMember,
  type PayrollRosterValue,
} from "@/payroll/roster/types";

export interface BulkImportRow {
  stId: string;
  value: string;
}

export interface BulkImportParseResult {
  rows: BulkImportRow[];
  errors: string[];
}

// Parses pasted rows keyed by ServiceTitan employee id, e.g. `19470,46.0` or a
// two-column paste (`st_id<TAB>value`). Blank lines are skipped; malformed
// lines are reported per line number and excluded from the parsed rows.
export const parseBulkImport = (text: string): BulkImportParseResult => {
  const rows: BulkImportRow[] = [];
  const errors: string[] = [];

  text.split(/\r?\n/).forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const lineNumber = index + 1;
    // Split on the first tab or comma so values may themselves contain commas.
    const match = trimmed.match(/^(.*?)[\t,](.*)$/);
    if (!match) {
      errors.push(`Line ${lineNumber}: expected "id, value"`);
      return;
    }

    const stId = match[1].trim();
    const value = match[2].trim();
    if (!stId) {
      errors.push(`Line ${lineNumber}: missing ServiceTitan id`);
      return;
    }
    if (!value) {
      errors.push(`Line ${lineNumber}: missing value`);
      return;
    }

    rows.push({ stId, value });
  });

  return { rows, errors };
};

export interface BulkImportApplyResult {
  members: PayrollRosterMember[];
  matched: string[];
  unmatched: string[];
  invalid: BulkImportInvalidRow[];
}

export interface BulkImportInvalidRow extends BulkImportRow {
  reason: string;
}

type NormalizedBulkImportValue =
  | { valid: true; value: PayrollRosterValue }
  | { valid: false; reason: string };

const normalizeBulkImportValue = (
  rawValue: string,
  field: PayrollRosterField,
): NormalizedBulkImportValue => {
  switch (field.field_type) {
    case PAYROLL_ROSTER_FIELD_TYPE.boolean: {
      const normalized = rawValue.trim().toLowerCase();
      if (["true", "yes", "1"].includes(normalized)) {
        return { valid: true, value: true };
      }
      if (["false", "no", "0"].includes(normalized)) {
        return { valid: true, value: false };
      }
      return { valid: false, reason: 'Expected "Yes" or "No"' };
    }
    case PAYROLL_ROSTER_FIELD_TYPE.fixedOption:
      return field.options.includes(rawValue)
        ? { valid: true, value: rawValue }
        : {
            valid: false,
            reason: `Choose one of: ${field.options.join(", ")}`,
          };
    case PAYROLL_ROSTER_FIELD_TYPE.number: {
      let normalized = rawValue.trim();
      if (field.format === "currency")
        normalized = normalized.replace(/[$,]/g, "");
      if (field.format === "percent") normalized = normalized.replace(/%$/, "");
      return /^\d+(?:\.\d+)?$/.test(normalized)
        ? { valid: true, value: normalized }
        : { valid: false, reason: "Expected a number" };
    }
    case PAYROLL_ROSTER_FIELD_TYPE.text:
      return { valid: true, value: rawValue };
  }
};

// Applies parsed rows to a field's value on each matching member (matched by
// service_titan_employee_id). Order and length of `members` are preserved so
// callers can zip the result back onto their drafts. Unmatched ids (rows whose
// id had no member) are reported.
export const applyBulkImportToMembers = (
  members: PayrollRosterMember[],
  field: PayrollRosterField,
  rows: BulkImportRow[],
): BulkImportApplyResult => {
  // Preserve the existing last-row-wins behavior for duplicate employee IDs.
  const rowByStId = new Map(rows.map((row) => [row.stId, row]));
  const normalizedByStId = new Map(
    Array.from(rowByStId.entries()).map(([stId, row]) => [
      stId,
      { row, result: normalizeBulkImportValue(row.value, field) },
    ]),
  );
  const matched: string[] = [];
  const invalid: BulkImportInvalidRow[] = [];

  normalizedByStId.forEach(({ row, result }) => {
    if (!result.valid) invalid.push({ ...row, reason: result.reason });
  });

  const updated = members.map((member) => {
    const normalized = normalizedByStId.get(member.service_titan_employee_id);
    if (!normalized || !normalized.result.valid) return member;
    matched.push(member.service_titan_employee_id);
    return {
      ...member,
      field_values: {
        ...member.field_values,
        [field.key]: normalized.result.value,
      },
    };
  });

  const matchedSet = new Set(matched);
  const unmatched = Array.from(normalizedByStId.entries())
    .filter(([, { result }]) => result.valid)
    .map(([stId]) => stId)
    .filter((stId) => !matchedSet.has(stId));

  return { members: updated, matched, unmatched, invalid };
};
